import { useCallback, useEffect } from "react";

import { downloadManagerOpenState } from "@keepeek/commons";
import {
  DownloadManagerConditionsProps,
  DownloadManagerCSUContent,
} from "@keepeek/refront-components";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { DownloadManager } from "../../../models/configuration/components/downloadManager";
import { useConfiguration } from "../../config/hooks";
import { useCurrentLocale } from "../../i18n/hooks";
import { downloadConditionsErrorState, downloadCSUState } from "../atoms/conditions";
import { downloadCSUSelector } from "../selectors/conditions";

export type UseDownloadConditions = {
  state: boolean;
  onChange: () => void;
  error: boolean;
  hasError: () => void;
  loading: boolean;
} & Pick<DownloadManagerConditionsProps, "cgu" | "csu">;

export const useDownloadConditions = (selectionId: string | null): UseDownloadConditions => {
  const locale = useCurrentLocale();
  // Extend DownloadManager types for retro compat
  const { enableCGU: enableCSURetro, conditions: conditionsAdmin } = useConfiguration<
    DownloadManager & { enableCGU: boolean | undefined }
  >(CONFIGURATION_SECTION_PATH.COMPONENTS_DOWNLOAD_MANAGER) ?? {
    enableCGU: undefined,
    conditions: undefined,
  };
  const [state, setState] = useRecoilState(downloadCSUState(selectionId));
  const [error, setError] = useRecoilState(downloadConditionsErrorState(selectionId));
  const openDM = useRecoilValue(downloadManagerOpenState);

  const csuContentsQuery = useRecoilValueLoadable(downloadCSUSelector(selectionId));
  const csuContents: DownloadManagerCSUContent[] =
    csuContentsQuery.state === "hasValue" ? csuContentsQuery.contents : [];

  const cguContent =
    conditionsAdmin?.cgu?.content?.find((c) => c.language === locale)?.value ||
    conditionsAdmin?.cgu?.content?.[0]?.value ||
    "";

  const cgu: UseDownloadConditions["cgu"] = {
    enable: conditionsAdmin?.cgu?.enableCGU ?? false,
    content: cguContent,
  };
  const csu: UseDownloadConditions["csu"] = {
    enable:
      (conditionsAdmin?.csu?.enableCSU !== undefined
        ? conditionsAdmin.csu.enableCSU
        : enableCSURetro || false) && csuContents.length > 0,
    content: csuContents,
  };

  const loading = csuContentsQuery.state === "loading";

  const hasError = useCallback(() => {
    setError(true);
  }, [setError]);

  const onChange = useCallback(() => {
    setState((state) => !state);
    setError(state);
  }, [setError, setState, state]);

  useEffect(() => {
    if (!openDM) {
      setError(false);
    }
  }, [openDM, setError, setState]);

  return {
    state,
    onChange,
    error,
    hasError,
    loading,
    csu,
    cgu,
  };
};
